/**
 * Local editable-grid store + its bridge to the server.
 *
 *   consumer ──handleRequest──▶ queue ──▶ server
 *        ▲                                  │
 *        └── resolve / reject ◀── reconcile ┘ (candidate | refresh)
 *
 * TWO DOORS — pick by who OWNS the rows, not by what is convenient.
 *
 *   Door A  this hook · handle.Provider              12 hosts
 *           the STORE owns the rows; the page has no rows slice
 *           ⚠ REPLACES the store on the context. A child under it can
 *             no longer read the page store AT ALL. Read page slices
 *             ABOVE the Provider and pass them down as props —
 *             PaymentAllocations and JournalEntryGrid both banner it.
 *
 *   Door B  StoreStateLensFocusProvider              4 hosts
 *           the PAGE owns the rows; the grid focuses a lens onto them
 *           remaps ONE slice KEY, leaves the store on the context —
 *           ExpensesGridV2 · LineItemsGrid · JournalEntrySection.
 *
 *   Both at once is legitimate exactly once: useLocalExpensesGrid opens
 *   Door A for the store, then uses Door B to re-point the `expenses`
 *   key at it, because ExpensesGridV2 reads through a pinned lens.
 *
 * Two grids on one page under Door A must NOT share a slice key: both
 * answer to EDITABLE_GRID_SLICE, so the second silently reads the
 * first's rows. Scope each Provider to exactly its own grid (see
 * InventoryTransferCell's header) — EditableDataGrid's dev-only
 * SHARED_SLICE_ERROR guard shouts when you get it wrong.
 *
 * `storeDoors.test.tsx` is the executable version of this block.
 *
 * BRIDGE CONTRACT — callback semantics (lead rulings R1/R2/R3, 2026-08-02)
 *
 *  R1 suppression   A held server candidate WINS over a failed request's
 *                   projection: that request's consumer `reject` is
 *                   suppressed ON PURPOSE, since replaying it would paint
 *                   stale local state over fresh server truth. Silent.
 *  R1 abandonment   Disposal · entity replacement · row disappearance also
 *                   drop the consumer callback — but NEVER silently. Each
 *                   emits a dev-mode warning naming the row and the cause,
 *                   and leaves NO pending caller state behind.
 *  R2 canonical-1st A consumer `resolve` that throws AFTER a server success
 *                   must not block reconciliation. Reconcile and publish
 *                   FIRST, then re-throw asynchronously onto the app's
 *                   global error path plus console.error in dev. Never
 *                   swallow, never a bespoke channel.
 *  R3 row errors    `rowErrors` holds ANY `Error`; only a `ZodError` carries
 *                   `.issues` — narrow before walking them (gridRows.store).
 */

import { type LensOpaqueType, lens, withLenses } from '@dhmk/zustand-lens';
import type { GridRowId } from '@mui/x-data-grid-premium';
import type { ComponentType, ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { RequestQueue, RequestQueueLifecycle } from 'src/hooks/useQueuedRequests';
import { useRequestQueue } from 'src/hooks/useQueuedRequests';
import {
  createGridRowsSlice,
  EDITABLE_GRID_SLICE,
  type EditableGridInitialState,
  type EditableGridState,
  type GridRow,
  type GridRowRemoveEvent,
  resolveValidationModel,
} from 'src/lib/gridRows.store';
import { StoreStateProvider } from 'src/providers/StoreStateProvider';
import { createStore, type Mutate, type StoreApi } from 'zustand';
import { immer } from 'zustand/middleware/immer';

type RemoteRow = { id?: number };
type MutationKind = 'create' | 'update' | 'delete';
type ImmerMutators = [['zustand/immer', never]];

export type EditableGridServerSnapshot<R extends RemoteRow> = {
  entityId: string | number;
  revision?: unknown;
  rows: readonly R[];
};

export type RefreshSnapshot<R extends RemoteRow> = { completeForScope?: true; revision?: unknown; rows: readonly R[] };

export type RefreshOutcome =
  | { status: 'applied'; requestId: number }
  | { status: 'superseded'; requestId: number }
  | { status: 'invalidated'; requestId: number };

export type UseLocalEditableGridStoreOptions<R extends RemoteRow> = {
  initialState: Pick<EditableGridInitialState<R>, 'capabilities' | 'validationModel'>;
  onAcceptedRow?: (row: GridRow<R>) => void;
  source: EditableGridServerSnapshot<R>;
};

export type LocalEditableGridStoreHandle<R extends RemoteRow, I> = {
  Provider: ComponentType<{ children: ReactNode }>;
  handleRequest: RequestQueue<R, I, MutationKind>;
  refresh: (load: () => Promise<RefreshSnapshot<R>>) => Promise<RefreshOutcome>;
  remove: (rowId: GridRowId) => GridRowRemoveEvent | undefined;
};

interface LocalEditableGridRoot<R extends RemoteRow> {
  [EDITABLE_GRID_SLICE]: LensOpaqueType<EditableGridState<R>, LocalEditableGridStore<R>>;
}

type LocalEditableGridStore<R extends RemoteRow> = Mutate<StoreApi<LocalEditableGridRoot<R>>, ImmerMutators>;
type RefreshTicket = { generation: number; requestId: number };
type RefreshTicketState = RefreshOutcome | { status: 'current' };
type HeldCandidate<R extends RemoteRow> =
  | { kind: 'present'; requestId: number; row: R; rowId: GridRowId }
  | { kind: 'absent'; requestId: number; rowId: GridRowId };
type PendingAdditions<R extends RemoteRow> = { requestId: number; rows: readonly R[] };
type RequestGuard = { rejectThroughRequestId: number };
type RefreshReconciliation<R extends RemoteRow> = {
  localRowsByRemoteId: Map<number, GridRow<R>>;
  orderedRowIds: Set<GridRowId>;
  originalRowUpdates: EditableGridState<R>['rowUpdates'];
  reconciledRowUpdates: EditableGridState<R>['rowUpdates'];
  reconciledRowIds: Set<GridRowId>;
};
type BridgeRequest<R extends RemoteRow> = {
  coalesced: boolean;
  generation: number;
  kind: MutationKind;
  ownsPendingCreate: boolean;
  reject: (error: Error) => void;
  remoteId?: number;
  resolve: (result: R) => void;
  rowId: GridRowId;
  token: number;
};
type DeferredRejection<R extends RemoteRow> = { error: Error; request: BridgeRequest<R> };

type LocalGridController<R extends RemoteRow> = {
  activate: () => void;
  cancelRequest: (token: number) => void;
  classifyRefresh: (ticket: RefreshTicket) => RefreshTicketState;
  dispose: () => void;
  isEntity: (entityId: string | number) => boolean;
  markRequestCoalesced: (token: number) => void;
  remove: (rowId: GridRowId) => GridRowRemoveEvent | undefined;
  registerRequest: (
    rowId: GridRowId,
    kind: MutationKind,
    resolve: (result: R) => void,
    reject: (error: Error) => void
  ) => number;
  rejectRequest: (token: number, error: Error) => void;
  replaceEntity: (source: EditableGridServerSnapshot<R>) => void;
  reserveRefresh: () => RefreshTicket;
  resolveRequest: (token: number, result: R) => void;
  settleRefresh: (ticket: RefreshTicket, snapshot: RefreshSnapshot<R>) => RefreshOutcome;
  settleRowIfIdle: (rowId: GridRowId) => void;
};

type LocalGridInstance<R extends RemoteRow> = { controller: LocalGridController<R>; store: LocalEditableGridStore<R> };

/** Why a consumer callback was dropped without being invoked. Each value is a real abandonment path and MUST be reported (R1); the candidate-wins suppression is absent from this union because it is contract, not abandonment. */
type AbandonmentCause = 'grid-disposed' | 'entity-replaced' | 'queue-disposed' | 'row-removed';

const normalizeRefreshError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('Grid refresh failed with a non-Error rejection', { cause: error });

const normalizeConsumerError = (error: unknown): Error =>
  error instanceof Error ? error : new Error('Grid consumer callback threw a non-Error value', { cause: error });

const isDevMode = (): boolean => process.env.NODE_ENV !== 'production';

/** The server write has already landed and reconciled, so a throwing consumer callback is never swallowed and never rolls it back: it is re-thrown on a fresh task onto the app's `window.onerror` path. */
const reportConsumerFailure = (error: unknown, context: { gridId: string; phase: string; rowId: GridRowId }): void => {
  const consumerError = normalizeConsumerError(error);

  if (isDevMode()) {
    console.error(
      `Editable grid consumer callback threw during ${context.phase} (grid ${context.gridId}, row ${String(context.rowId)}). ` +
        'The server write and the bridge reconciliation both completed; only the callback failed.',
      consumerError
    );
  }

  setTimeout(() => {
    throw consumerError;
  }, 0);
};

/** Runs a consumer callback so a throw can never strand the bridge mid-reconciliation. */
const runConsumerCallback = (
  callback: () => void,
  context: { gridId: string; phase: string; rowId: GridRowId }
): void => {
  try {
    callback();
  } catch (error) {
    reportConsumerFailure(error, context);
  }
};

const validateServerRows = (rows: readonly RemoteRow[]): void => {
  const remoteIds = new Set<number>();
  rows.forEach((row) => {
    if (typeof row.id !== 'number' || !Number.isFinite(row.id)) {
      throw new Error('Grid server rows require a numeric remote id');
    }
    if (remoteIds.has(row.id)) {
      throw new Error(`Grid refresh contains duplicate remote id ${row.id}`);
    }
    remoteIds.add(row.id);
  });
};

function createLocalGridInstance<R extends RemoteRow, I>(
  source: EditableGridServerSnapshot<R>,
  getInitialState: () => UseLocalEditableGridStoreOptions<R>['initialState'],
  queue: RequestQueueLifecycle<R, I, MutationKind>,
  publishAccepted: (row: GridRow<R>) => void
): LocalGridInstance<R> {
  validateServerRows(source.rows);
  let controller: LocalGridController<R> | undefined;

  const store: LocalEditableGridStore<R> = createStore<LocalEditableGridRoot<R>>()(
    immer(
      withLenses<LocalEditableGridRoot<R>, ImmerMutators>(() => ({
        [EDITABLE_GRID_SLICE]: lens<EditableGridState<R>, LocalEditableGridStore<R>>((set, get) => {
          let candidates = new Map<GridRowId, HeldCandidate<R>>();
          let currentEntityId = source.entityId;
          let deferredRejections = new Map<GridRowId, DeferredRejection<R>[]>();
          let deleteTombstones = new Map<number, RequestGuard>();
          let disposed = false;
          let generation = 0;
          let latestAppliedRows: readonly R[] = source.rows;
          let latestRequestId = 0;
          let locallyDeletedDrafts = new Set<GridRowId>();
          let nextRequestToken = 1;
          let pendingAdditions: PendingAdditions<R> | undefined;
          let pendingCreateRows = new Set<GridRowId>();
          let requests = new Map<number, BridgeRequest<R>>();
          let rowOrder: GridRowId[] = [];
          let visibilityGuards = new Map<number, RequestGuard>();

          const gridId = () => String(currentEntityId);

          /**
           * R1: an abandoned caller can never distinguish "still pending" from "gone",
           * which is how stuck spinners and leaks happen. Name the row and the cause.
           */
          const warnAbandoned = (cause: AbandonmentCause, rowId: GridRowId, droppedCallbacks: number) => {
            if (!isDevMode() || droppedCallbacks === 0) {
              return;
            }

            console.warn(
              `Editable grid abandoned ${droppedCallbacks} pending request callback(s) for row ${String(rowId)} ` +
                `in grid ${gridId()} (cause: ${cause}). Their resolve/reject will never be invoked — see the ` +
                'bridge contract at the top of useLocalEditableGridStore.tsx.'
            );
          };

          /** Every caller callback still held by the bridge, grouped per row. */
          const warnAbandonedAll = (cause: AbandonmentCause) => {
            if (!isDevMode()) {
              return;
            }

            const droppedByRow = new Map<GridRowId, number>();
            requests.forEach((request) => {
              droppedByRow.set(request.rowId, (droppedByRow.get(request.rowId) ?? 0) + 1);
            });
            deferredRejections.forEach((rowRejections, rowId) => {
              droppedByRow.set(rowId, (droppedByRow.get(rowId) ?? 0) + rowRejections.length);
            });
            droppedByRow.forEach((droppedCallbacks, rowId) => warnAbandoned(cause, rowId, droppedCallbacks));
          };

          const rebuildInitialRows = (state: EditableGridState<R>) => {
            const seen = new Set<GridRowId>();
            const orderedRows: GridRow<R>[] = [];
            rowOrder.forEach((rowId) => {
              const row = state.localRows[rowId];
              if (row && !seen.has(rowId)) {
                seen.add(rowId);
                orderedRows.push(row);
              }
            });
            Object.values(state.localRows).forEach((row) => {
              if (!seen.has(row.rowId)) {
                seen.add(row.rowId);
                orderedRows.push(row);
                rowOrder.push(row.rowId);
              }
            });
            state.initialRows = orderedRows;
          };

          const createRefreshReconciliation = (state: EditableGridState<R>): RefreshReconciliation<R> => {
            const localRowsByRemoteId = new Map<number, GridRow<R>>();
            Object.values(state.localRows).forEach((row) => {
              if (row.id !== undefined) {
                localRowsByRemoteId.set(row.id, row);
              }
            });
            return {
              localRowsByRemoteId,
              orderedRowIds: new Set(rowOrder),
              originalRowUpdates: state.rowUpdates,
              reconciledRowUpdates: [],
              reconciledRowIds: new Set(),
            };
          };

          const finishRefreshReconciliation = (
            state: EditableGridState<R>,
            reconciliation: RefreshReconciliation<R>
          ) => {
            if (reconciliation.reconciledRowIds.size === 0) {
              return;
            }
            state.rowUpdates = [
              ...reconciliation.originalRowUpdates.filter(
                (update) => !reconciliation.reconciledRowIds.has(update.rowId)
              ),
              ...reconciliation.reconciledRowUpdates,
            ];
          };

          const reorderFromServer = (
            state: EditableGridState<R>,
            rows: readonly R[],
            reconciliation?: RefreshReconciliation<R>
          ) => {
            const localRowsByRemoteId =
              reconciliation?.localRowsByRemoteId ?? createRefreshReconciliation(state).localRowsByRemoteId;
            const nextOrder: GridRowId[] = [];
            const seen = new Set<GridRowId>();
            rows.forEach((remoteRow) => {
              const localRow = remoteRow.id === undefined ? undefined : localRowsByRemoteId.get(remoteRow.id);
              if (localRow && !seen.has(localRow.rowId)) {
                seen.add(localRow.rowId);
                nextOrder.push(localRow.rowId);
              }
            });
            rowOrder.forEach((rowId) => {
              if (state.localRows[rowId] && !seen.has(rowId)) {
                seen.add(rowId);
                nextOrder.push(rowId);
              }
            });
            rowOrder = nextOrder;
            rebuildInitialRows(state);
          };

          const applyPresentRow = (
            state: EditableGridState<R>,
            remoteRow: R,
            existingRowId?: GridRowId,
            reconciliation?: RefreshReconciliation<R>
          ) => {
            const rowId = existingRowId ?? ++state.nextRowId;
            const action: 'create' | 'update' = existingRowId === undefined ? 'create' : 'update';
            const accepted: GridRow<R> = {
              ...remoteRow,
              isCreating: false,
              isDeleted: false,
              isDraft: false,
              rowId,
              status: 'accepted',
            };
            state.localRows[rowId] = accepted;
            state.rowSnapshots[Number(rowId)] = accepted;
            delete state.rowPatches[Number(rowId)];
            // The model may be row-dependent (1c) — resolve it against the accepted row.
            const validationModel = getInitialState().validationModel;
            const validation = validationModel
              ? resolveValidationModel(validationModel, { row: accepted }).safeParse(accepted)
              : undefined;
            if (validation && !validation.success) {
              state.rowErrors[Number(rowId)] = validation.error;
            } else {
              delete state.rowErrors[Number(rowId)];
            }
            const interaction = state.rowInteractionModel[Number(rowId)];
            state.rowInteractionModel[Number(rowId)] = { saved: true, visited: interaction?.visited ?? new Set() };
            if (reconciliation) {
              if (accepted.id !== undefined) {
                reconciliation.localRowsByRemoteId.set(accepted.id, accepted);
              }
              reconciliation.reconciledRowIds.add(rowId);
              reconciliation.reconciledRowUpdates.push({ ...accepted, _action: action });
              if (!reconciliation.orderedRowIds.has(rowId)) {
                reconciliation.orderedRowIds.add(rowId);
                rowOrder.push(rowId);
              }
            } else {
              state.rowUpdates = [
                ...state.rowUpdates.filter((update) => update.rowId !== rowId),
                { ...accepted, _action: action },
              ];
              if (!rowOrder.includes(rowId)) {
                rowOrder.push(rowId);
              }
            }
          };

          const applyAbsentRow = (
            state: EditableGridState<R>,
            rowId: GridRowId,
            reconciliation?: RefreshReconciliation<R>
          ) => {
            const localRow = state.localRows[rowId];
            if (!localRow) {
              return;
            }
            delete state.localRows[rowId];
            delete state.rowSnapshots[Number(rowId)];
            delete state.rowPatches[Number(rowId)];
            delete state.rowErrors[Number(rowId)];
            delete state.rowInteractionModel[Number(rowId)];
            if (reconciliation) {
              if (localRow.id !== undefined) {
                reconciliation.localRowsByRemoteId.delete(localRow.id);
              }
              reconciliation.orderedRowIds.delete(rowId);
              reconciliation.reconciledRowIds.add(rowId);
              reconciliation.reconciledRowUpdates.push({ rowId, _action: 'delete' });
            } else {
              rowOrder = rowOrder.filter((candidateRowId) => candidateRowId !== rowId);
              state.rowUpdates = [
                ...state.rowUpdates.filter((update) => update.rowId !== rowId),
                { rowId, _action: 'delete' },
              ];
            }
          };

          const canApplyPresence = (requestId: number, remoteId: number) => {
            const guard = visibilityGuards.get(remoteId);
            if (guard) {
              if (requestId <= guard.rejectThroughRequestId) {
                return false;
              }
              visibilityGuards.delete(remoteId);
            }
            const tombstone = deleteTombstones.get(remoteId);
            if (tombstone) {
              if (requestId <= tombstone.rejectThroughRequestId) {
                return false;
              }
              deleteTombstones.delete(remoteId);
            }
            return true;
          };

          const canApplyCompleteAbsence = (requestId: number, row: GridRow<R>) => {
            if (row.id === undefined) {
              return false;
            }
            const guard = visibilityGuards.get(row.id);
            if (!guard) {
              return true;
            }
            if (requestId <= guard.rejectThroughRequestId) {
              return false;
            }
            visibilityGuards.delete(row.id);
            return true;
          };

          const releasePendingAdditions = (state: EditableGridState<R>, excludedRemoteId?: number) => {
            const pending = pendingAdditions;
            if (!pending || pendingCreateRows.size > 0) {
              return;
            }
            const reconciliation = createRefreshReconciliation(state);
            pendingAdditions = undefined;
            pending.rows.forEach((remoteRow) => {
              if (remoteRow.id === undefined || remoteRow.id === excludedRemoteId) {
                return;
              }
              if (!canApplyPresence(pending.requestId, remoteRow.id)) {
                return;
              }
              const localRow = reconciliation.localRowsByRemoteId.get(remoteRow.id);
              if (localRow && queue.isRowPending(localRow.rowId)) {
                candidates.set(localRow.rowId, {
                  kind: 'present',
                  requestId: pending.requestId,
                  row: remoteRow,
                  rowId: localRow.rowId,
                });
              } else {
                applyPresentRow(state, remoteRow, localRow?.rowId, reconciliation);
              }
            });
            finishRefreshReconciliation(state, reconciliation);
          };

          const removeDeferredRequest = (token: number) => {
            deferredRejections.forEach((rowRejections, rowId) => {
              const remaining = rowRejections.filter((entry) => entry.request.token !== token);
              if (remaining.length) {
                deferredRejections.set(rowId, remaining);
              } else {
                deferredRejections.delete(rowId);
              }
            });
          };

          const settleRowIfIdle = (rowId: GridRowId) => {
            if (queue.isRowPending(rowId)) {
              return;
            }
            const rejections = deferredRejections.get(rowId) ?? [];
            const candidate = candidates.get(rowId);
            deferredRejections.delete(rowId);
            candidates.delete(rowId);
            const settledCreateOwner = rejections.some(
              ({ request }) =>
                request.generation === generation && request.kind === 'create' && request.ownsPendingCreate
            );
            if (settledCreateOwner) {
              pendingCreateRows.delete(rowId);
            }

            const liveRow = get().localRows[rowId];
            if (!liveRow) {
              // R1 abandonment: the row is gone, so there is nothing left to project a
              // rejection onto. The callbacks die here — say so instead of going quiet.
              warnAbandoned('row-removed', rowId, rejections.length);
              if (locallyDeletedDrafts.has(rowId) && settledCreateOwner) {
                pendingAdditions = undefined;
              }
              return;
            }

            if (candidate) {
              // R1 suppression (contract, not a bug): the server candidate is canonical, so `rejections` are dropped rather than repainting the row with the rejected local projection.
              set((state) => {
                if (candidate.kind === 'present') {
                  applyPresentRow(state, candidate.row, candidate.rowId);
                } else {
                  applyAbsentRow(state, candidate.rowId);
                }
                releasePendingAdditions(state);
                reorderFromServer(state, latestAppliedRows);
              });
              return;
            }

            rejections
              .filter(({ request }) => request.generation === generation)
              .forEach(({ error, request }) =>
                // A throwing consumer reject must not strand the rejections behind it.
                runConsumerCallback(() => request.reject(error), {
                  gridId: gridId(),
                  phase: 'request rejection',
                  rowId: request.rowId,
                })
              );
            if (pendingAdditions && pendingCreateRows.size === 0) {
              set((state) => {
                releasePendingAdditions(state);
                reorderFromServer(state, latestAppliedRows);
              });
            }
          };

          const buildCanonicalRow = (result: R, rowId: GridRowId, currentRemoteId?: number): GridRow<R> => ({
            ...result,
            ...(result.id === undefined && currentRemoteId !== undefined ? { id: currentRemoteId } : {}),
            isCreating: false,
            isDeleted: false,
            isDraft: false,
            rowId,
            status: 'accepted',
          });

          const resolveRequest = (token: number, result: R) => {
            const request = requests.get(token);
            if (!request || disposed || request.generation !== generation) {
              return;
            }
            requests.delete(token);

            if (request.coalesced) {
              // No bridge reconciliation is owed for a coalesced follower; still report.
              runConsumerCallback(() => request.resolve(result), {
                gridId: gridId(),
                phase: 'coalesced resolve',
                rowId: request.rowId,
              });
              return;
            }

            const canonical = buildCanonicalRow(result, request.rowId, request.remoteId);
            // R2: the write already landed. Capture a throwing consumer callback, finish
            // reconciliation and publication, and only then report it out of band —
            // bailing here would leave the UI disagreeing with the database.
            let consumerFailure: { error: unknown } | undefined;
            try {
              request.resolve(result);
            } catch (error) {
              consumerFailure = { error };
            } finally {
              candidates.delete(request.rowId);
              deferredRejections.delete(request.rowId);
              if (request.kind === 'create' && request.ownsPendingCreate) {
                pendingCreateRows.delete(request.rowId);
              }
              if (request.kind === 'delete') {
                if (request.remoteId !== undefined) {
                  visibilityGuards.delete(request.remoteId);
                  deleteTombstones.set(request.remoteId, { rejectThroughRequestId: latestRequestId });
                }
              } else {
                const remoteId = canonical.id ?? request.remoteId;
                if (remoteId !== undefined) {
                  visibilityGuards.set(remoteId, { rejectThroughRequestId: latestRequestId });
                }
              }
            }

            if (request.kind !== 'delete') {
              const remoteId = canonical.id ?? request.remoteId;
              set((state) => {
                const interaction = state.rowInteractionModel[Number(request.rowId)];
                if (state.localRows[request.rowId]) {
                  state.rowInteractionModel[Number(request.rowId)] = {
                    saved: true,
                    visited: interaction?.visited ?? new Set(),
                  };
                }
                releasePendingAdditions(state, remoteId);
                reorderFromServer(state, latestAppliedRows);
              });
              locallyDeletedDrafts.delete(request.rowId);
              runConsumerCallback(() => publishAccepted(canonical), {
                gridId: gridId(),
                phase: 'accepted-row publication',
                rowId: request.rowId,
              });
            }

            // R2 canonical-first: reconciliation and publication are complete, so the
            // consumer's exception can now be surfaced without manufacturing divergence.
            if (consumerFailure) {
              reportConsumerFailure(consumerFailure.error, {
                gridId: gridId(),
                phase: `${request.kind} resolve`,
                rowId: request.rowId,
              });
            }
          };

          const registerRequest = (
            rowId: GridRowId,
            kind: MutationKind,
            resolve: (result: R) => void,
            reject: (error: Error) => void
          ) => {
            const token = nextRequestToken++;
            const ownsPendingCreate = kind === 'create' && !pendingCreateRows.has(rowId);
            if (kind === 'create') {
              pendingCreateRows.add(rowId);
            }
            requests.set(token, {
              coalesced: false,
              generation,
              kind,
              ownsPendingCreate,
              reject,
              remoteId: get().localRows[rowId]?.id,
              resolve,
              rowId,
              token,
            });
            return token;
          };

          const rejectRequest = (token: number, error: Error) => {
            const request = requests.get(token);
            if (!request || disposed || request.generation !== generation) {
              return;
            }
            requests.delete(token);
            const rowRejections = deferredRejections.get(request.rowId) ?? [];
            rowRejections.push({ error, request });
            deferredRejections.set(request.rowId, rowRejections);
          };

          const applyRefresh = (ticket: RefreshTicket, snapshot: RefreshSnapshot<R>): RefreshOutcome => {
            const ticketState = controller?.classifyRefresh(ticket);
            if (!ticketState || ticketState.status !== 'current') {
              return ticketState ?? { status: 'invalidated', requestId: ticket.requestId };
            }
            validateServerRows(snapshot.rows);
            let didAcceptSnapshotData = false;
            let didApplyVisibleRow = false;
            set((state) => {
              const reconciliation = createRefreshReconciliation(state);
              const remoteIds = new Set<number>();
              const heldAdditions: R[] = [];

              snapshot.rows.forEach((remoteRow) => {
                if (remoteRow.id === undefined) {
                  return;
                }
                remoteIds.add(remoteRow.id);
                if (!canApplyPresence(ticket.requestId, remoteRow.id)) {
                  return;
                }
                didAcceptSnapshotData = true;
                const localRow = reconciliation.localRowsByRemoteId.get(remoteRow.id);
                if (localRow && queue.isRowPending(localRow.rowId)) {
                  candidates.set(localRow.rowId, {
                    kind: 'present',
                    requestId: ticket.requestId,
                    row: remoteRow,
                    rowId: localRow.rowId,
                  });
                } else if (!localRow && pendingCreateRows.size > 0) {
                  heldAdditions.push(remoteRow);
                } else {
                  applyPresentRow(state, remoteRow, localRow?.rowId, reconciliation);
                  didApplyVisibleRow = true;
                }
              });

              if (snapshot.completeForScope === true) {
                Object.values(state.localRows).forEach((localRow) => {
                  if (
                    localRow.id === undefined ||
                    remoteIds.has(localRow.id) ||
                    !canApplyCompleteAbsence(ticket.requestId, localRow)
                  ) {
                    return;
                  }
                  didAcceptSnapshotData = true;
                  if (queue.isRowPending(localRow.rowId)) {
                    candidates.set(localRow.rowId, {
                      kind: 'absent',
                      requestId: ticket.requestId,
                      rowId: localRow.rowId,
                    });
                  } else {
                    applyAbsentRow(state, localRow.rowId, reconciliation);
                    didApplyVisibleRow = true;
                  }
                });
                deleteTombstones.forEach((tombstone, remoteId) => {
                  if (ticket.requestId > tombstone.rejectThroughRequestId && !remoteIds.has(remoteId)) {
                    deleteTombstones.delete(remoteId);
                  }
                });
              }

              pendingAdditions = heldAdditions.length
                ? { requestId: ticket.requestId, rows: heldAdditions }
                : undefined;
              if (didApplyVisibleRow) {
                finishRefreshReconciliation(state, reconciliation);
                reorderFromServer(state, snapshot.rows, reconciliation);
              }
            });
            if (didAcceptSnapshotData) {
              latestAppliedRows = snapshot.rows;
            }
            return { status: 'applied', requestId: ticket.requestId };
          };

          const createGridRowsCore = (rows: readonly R[]) => {
            const gridRowsState = createGridRowsSlice<R, EditableGridInitialState<R>>(set, get, {
              ...getInitialState(),
              rows: [...rows],
            });
            rowOrder = gridRowsState.initialRows.map((row) => row.rowId);
            return gridRowsState;
          };

          const initialGridRowsState = createGridRowsCore(source.rows);
          controller = {
            activate: () => {
              disposed = false;
            },
            cancelRequest: (token) => {
              const request = requests.get(token);
              requests.delete(token);
              removeDeferredRequest(token);
              if (request?.kind === 'create' && request.ownsPendingCreate) {
                pendingCreateRows.delete(request.rowId);
              }
              if (request) {
                // R1 abandonment: the queue refused the work outright (torn down).
                warnAbandoned('queue-disposed', request.rowId, 1);
              }
            },
            classifyRefresh: (ticket) => {
              if (disposed || ticket.generation !== generation) {
                return { status: 'invalidated', requestId: ticket.requestId };
              }
              return ticket.requestId === latestRequestId
                ? { status: 'current' }
                : { status: 'superseded', requestId: ticket.requestId };
            },
            dispose: () => {
              if (disposed) {
                return;
              }
              // R1 abandonment: report BEFORE the maps are cleared, while the rows are
              // still nameable. Nothing survives this, so no caller state is left behind.
              warnAbandonedAll('grid-disposed');
              disposed = true;
              generation++;
              candidates.clear();
              deferredRejections.clear();
              deleteTombstones.clear();
              locallyDeletedDrafts.clear();
              pendingAdditions = undefined;
              pendingCreateRows.clear();
              requests.clear();
              visibilityGuards.clear();
            },
            isEntity: (entityId) => currentEntityId === entityId,
            markRequestCoalesced: (token) => {
              const request = requests.get(token);
              if (request) {
                request.coalesced = true;
              }
            },
            remove: (rowId) => {
              const row = get().localRows[rowId];
              if (!row) {
                return undefined;
              }
              if (row.id !== undefined) {
                return get().remove(rowId);
              }
              locallyDeletedDrafts.add(rowId);
              candidates.delete(rowId);
              deferredRejections.delete(rowId);
              set((state) => {
                applyAbsentRow(state, rowId);
                rebuildInitialRows(state);
              });
              return undefined;
            },
            registerRequest,
            rejectRequest,
            replaceEntity: (nextSource) => {
              if (currentEntityId === nextSource.entityId) {
                return;
              }
              validateServerRows(nextSource.rows);
              // R1 abandonment: name the grid being torn down, not its replacement.
              warnAbandonedAll('entity-replaced');
              generation++;
              currentEntityId = nextSource.entityId;
              candidates = new Map();
              deferredRejections = new Map();
              deleteTombstones = new Map();
              latestAppliedRows = nextSource.rows;
              locallyDeletedDrafts = new Set();
              pendingAdditions = undefined;
              pendingCreateRows = new Set();
              requests = new Map();
              visibilityGuards = new Map();
              const nextGridRowsState = createGridRowsCore(nextSource.rows);
              set((state) => Object.assign(state, nextGridRowsState));
            },
            reserveRefresh: () => {
              const requestId = ++latestRequestId;
              candidates.clear();
              pendingAdditions = undefined;
              return { generation, requestId };
            },
            resolveRequest,
            settleRefresh: applyRefresh,
            settleRowIfIdle,
          };
          return initialGridRowsState;
        }),
      }))
    )
  );

  if (!controller) {
    throw new Error('Local editable grid controller was not initialized');
  }
  return { controller, store };
}

export function useLocalEditableGridStore<R extends RemoteRow, I>(
  options: UseLocalEditableGridStoreOptions<R>
): LocalEditableGridStoreHandle<R, I> {
  const { initialState, onAcceptedRow, source } = options;
  const latestInitialStateRef = useRef(initialState);
  const latestSourceRef = useRef(source);
  const onAcceptedRowRef = useRef(onAcceptedRow);
  latestInitialStateRef.current = initialState;
  latestSourceRef.current = source;
  onAcceptedRowRef.current = onAcceptedRow;

  const queue = useRequestQueue<R, I, MutationKind>();
  const instanceRef = useRef<LocalGridInstance<R> | null>(null);
  if (instanceRef.current === null) {
    instanceRef.current = createLocalGridInstance(
      source,
      () => latestInitialStateRef.current,
      queue,
      (row) => onAcceptedRowRef.current?.(row)
    );
  }
  const instance = instanceRef.current;

  const handleRequest = useCallback<RequestQueue<R, I, MutationKind>>(
    (rowId, request) => {
      const token = instance.controller.registerRequest(rowId, request.eventType, request.resolve, request.reject);
      const disposition = queue(rowId, {
        ...request,
        reject: (error) => instance.controller.rejectRequest(token, error),
        resolve: (result: R) => instance.controller.resolveRequest(token, result),
      });
      if (disposition === 'coalesced') {
        instance.controller.markRequestCoalesced(token);
      } else if (disposition === 'disposed') {
        instance.controller.cancelRequest(token);
      } else if (!queue.isRowPending(rowId)) {
        instance.controller.settleRowIfIdle(rowId);
      }
      return disposition;
    },
    [instance, queue]
  );

  const sourceEntityId = source.entityId;

  useLayoutEffect(() => {
    const nextSource = latestSourceRef.current;
    if (!instance.controller.isEntity(sourceEntityId)) {
      instance.controller.replaceEntity(nextSource);
      queue.reset();
    }
  }, [instance, queue, sourceEntityId]);

  useEffect(() => {
    const unsubscribe = queue.subscribeRowIdle((rowId) => instance.controller.settleRowIfIdle(rowId));
    instance.controller.activate();
    queue.activate();
    return () => {
      unsubscribe();
      instance.controller.dispose();
      queue.dispose();
    };
  }, [instance, queue]);

  const Provider = useMemo<ComponentType<{ children: ReactNode }>>(
    () =>
      function LocalEditableGridStoreProvider({ children }) {
        return (
          <StoreStateProvider<LocalEditableGridRoot<R>, ImmerMutators> storeFactory={() => instance.store}>
            {children}
          </StoreStateProvider>
        );
      },
    [instance]
  );

  const refresh = useCallback(
    async (load: () => Promise<RefreshSnapshot<R>>): Promise<RefreshOutcome> => {
      const ticket = instance.controller.reserveRefresh();
      try {
        const snapshot = await load();
        return instance.controller.settleRefresh(ticket, snapshot);
      } catch (error) {
        const ticketState = instance.controller.classifyRefresh(ticket);
        if (ticketState.status === 'superseded' || ticketState.status === 'invalidated') {
          return ticketState;
        }
        throw normalizeRefreshError(error);
      }
    },
    [instance]
  );

  return { Provider, handleRequest, refresh, remove: instance.controller.remove };
}
